
import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Save, User, Camera, Users } from 'lucide-react';

const ParentProfileForm: React.FC = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    // Basic Information
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    username: profile?.username || internalUser?.username || '',
    phone: profile?.phone || '',
    
    // Parent specific fields
    children_info: profile?.children_info || '',
    involvement_level: profile?.involvement_level || '',
    volunteer_interests: profile?.volunteer_interests || [],
    coaching_interest: profile?.coaching_interest || false,
    availability: profile?.availability || [],
    emergency_contact_name: profile?.emergency_contact_name || '',
    emergency_contact_phone: profile?.emergency_contact_phone || '',
    
    // Sports preferences
    preferred_sports: profile?.preferred_sports || [],
    
    // Profile Photo
    profile_photo: null as File | null,
    profile_photo_url: profile?.profile_photo_url || '',
    
    // Privacy settings
    show_profile_public: profile?.show_profile_public || false,
    show_contact_public: profile?.show_contact_public || false,
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, profile_photo: file }));
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleArrayChange = (field: string, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], value]
        : prev[field].filter((item: string) => item !== value)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      toast.success('Parent profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const volunteerOptions = [
    'Team Manager', 'Event Coordinator', 'Fundraising', 'Transportation',
    'Equipment Management', 'Concessions', 'Photography', 'Social Media'
  ];

  const availabilityOptions = [
    'Weekday Mornings', 'Weekday Evenings', 'Weekend Mornings', 'Weekend Evenings',
    'School Holidays', 'Summer Break', 'Tournament Days'
  ];

  const sportsOptions = [
    'Football', 'Basketball', 'Soccer', 'Baseball', 'Tennis', 
    'Golf', 'Swimming', 'Track & Field', 'Volleyball', 'Hockey'
  ];

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Photo Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Profile Photo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                {photoPreview || formData.profile_photo_url ? (
                  <img 
                    src={photoPreview || formData.profile_photo_url} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="h-12 w-12 text-gray-400" />
                )}
              </div>
              <div>
                <Label htmlFor="profile_photo" className="cursor-pointer">
                  <div className="bg-sports-green text-black px-4 py-2 rounded-lg hover:bg-green-600 transition-colors inline-flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    Upload Photo
                  </div>
                </Label>
                <Input
                  id="profile_photo"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-gray-300">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-gray-300">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="username" className="text-gray-300">Username *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-gray-300">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Parent Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="h-5 w-5" />
              Parent Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="children_info" className="text-gray-300">Children Information</Label>
              <Textarea
                id="children_info"
                value={formData.children_info}
                onChange={(e) => handleInputChange('children_info', e.target.value)}
                placeholder="Tell us about your children (names, ages, sports they play)..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="involvement_level" className="text-gray-300">Desired Involvement Level</Label>
              <Textarea
                id="involvement_level"
                value={formData.involvement_level}
                onChange={(e) => handleInputChange('involvement_level', e.target.value)}
                placeholder="How would you like to be involved in your child's sports activities?"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Interested in Coaching</Label>
                <p className="text-sm text-gray-400">Would you be interested in coaching opportunities?</p>
              </div>
              <Switch
                checked={formData.coaching_interest}
                onCheckedChange={(checked) => handleInputChange('coaching_interest', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Volunteer Interests */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Volunteer Interests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {volunteerOptions.map((volunteer) => (
                <div key={volunteer} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={volunteer}
                    checked={formData.volunteer_interests.includes(volunteer)}
                    onChange={(e) => handleArrayChange('volunteer_interests', volunteer, e.target.checked)}
                    className="w-4 h-4 text-sports-green bg-gray-700 border-gray-600 rounded focus:ring-sports-green"
                  />
                  <Label htmlFor={volunteer} className="text-gray-300 text-sm cursor-pointer">
                    {volunteer}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Availability */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Availability for Volunteer Activities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {availabilityOptions.map((time) => (
                <div key={time} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={time}
                    checked={formData.availability.includes(time)}
                    onChange={(e) => handleArrayChange('availability', time, e.target.checked)}
                    className="w-4 h-4 text-sports-green bg-gray-700 border-gray-600 rounded focus:ring-sports-green"
                  />
                  <Label htmlFor={time} className="text-gray-300 text-sm cursor-pointer">
                    {time}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Sports Preferences */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Sports of Interest</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {sportsOptions.map((sport) => (
                <div key={sport} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={sport}
                    checked={formData.preferred_sports.includes(sport)}
                    onChange={(e) => handleArrayChange('preferred_sports', sport, e.target.checked)}
                    className="w-4 h-4 text-sports-green bg-gray-700 border-gray-600 rounded focus:ring-sports-green"
                  />
                  <Label htmlFor={sport} className="text-gray-300 text-sm cursor-pointer">
                    {sport}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Emergency Contact */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Emergency Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="emergency_contact_name" className="text-gray-300">Emergency Contact Name</Label>
                <Input
                  id="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="emergency_contact_phone" className="text-gray-300">Emergency Contact Phone</Label>
                <Input
                  id="emergency_contact_phone"
                  type="tel"
                  value={formData.emergency_contact_phone}
                  onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Privacy Settings */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Privacy Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Make Profile Public</Label>
                <p className="text-sm text-gray-400">Allow others to view your profile</p>
              </div>
              <Switch
                checked={formData.show_profile_public}
                onCheckedChange={(checked) => handleInputChange('show_profile_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Contact Information</Label>
                <p className="text-sm text-gray-400">Display contact info publicly</p>
              </div>
              <Switch
                checked={formData.show_contact_public}
                onCheckedChange={(checked) => handleInputChange('show_contact_public', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className="bg-sports-green text-black hover:bg-green-600"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Parent Profile
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ParentProfileForm;
